import {
  NodeAndOverride,
  getStudentNumbersOrOverridden,
} from './node-and-override';

export enum HierarchyAndStudentNumbersTransfer {
  NoTransfer = 'NoTransfer',
  FromLegacyToFirstExistingNode = 'FromLegacyToFirstExistingNode',
  FromListToFirstNewlyAddedNode = 'FromListToFirstNewlyAddedNode',
}

export type HierarchyOnlyStudentNumbers = {
  listStudentNumbers: null;
  nodesAndOverrides: NodeAndOverride[];
  initialStatus: HierarchyAndStudentNumbersTransfer;
};
export type ListOnlyStudentNumbers = {
  listStudentNumbers: number;
  nodesAndOverrides: null;
  initialStatus: HierarchyAndStudentNumbersTransfer;
};
export type NoHierarchyOrStudentNumbers = {
  listStudentNumbers: null;
  nodesAndOverrides: null;
  initialStatus: HierarchyAndStudentNumbersTransfer;
};

/**
 * Note: HierarchyAndStudentNumbers forbids that both `listStudentNumbers`
 * and `nodesAndOverrides` be non-empty. Use `RawHierarchyAndStudentNumbers`
 * for that case.
 */
export type HierarchyAndStudentNumbers =
  | HierarchyOnlyStudentNumbers
  | ListOnlyStudentNumbers
  | NoHierarchyOrStudentNumbers;

export type RawHierarchyAndStudentNumbers = {
  listStudentNumbers: number | null;
  nodesAndOverrides: NodeAndOverride[] | null;
  initialStatus: HierarchyAndStudentNumbersTransfer;
};

export function getTotalStudentNumbers(
  hierarchyAndStudentNumbers: HierarchyAndStudentNumbers | null,
): number {
  if (!hierarchyAndStudentNumbers) {
    return 0;
  } else if (hierarchyAndStudentNumbers.listStudentNumbers) {
    return hierarchyAndStudentNumbers.listStudentNumbers;
  } else if (!hierarchyAndStudentNumbers.nodesAndOverrides) {
    return 0;
  }

  const reduceStudentNumbers = (
    soFar: number,
    nodeAndOverride: NodeAndOverride,
  ) => soFar + getStudentNumbersOrOverridden(nodeAndOverride);

  return hierarchyAndStudentNumbers.nodesAndOverrides.reduce(
    reduceStudentNumbers,
    0,
  );
}

/**
 * Normalise a raw hierarchy and student numbers instance generated by user
 * interactions with the UI, where `listStudentNumbers` will be normalised
 * away when nodes are specified.
 *
 * @param  {RawHierarchyAndStudentNumbers} rawHierarchyAndStudentNumbers Raw hierarchy and student number data
 * @return {HierarchyAndStudentNumbers}                                  Normalised hierarchy and student number data
 */
export function normaliseHierarchyAndStudentNumbers(
  rawHierarchyAndStudentNumbers: RawHierarchyAndStudentNumbers,
): HierarchyAndStudentNumbers {
  const { initialStatus, listStudentNumbers, nodesAndOverrides } =
    rawHierarchyAndStudentNumbers;
  if (Number.isInteger(listStudentNumbers) && listStudentNumbers > 0) {
    if (!nodesAndOverrides || nodesAndOverrides.length === 0) {
      return {
        initialStatus,
        listStudentNumbers,
        nodesAndOverrides: null,
      };
    } else {
      return {
        initialStatus,
        listStudentNumbers: null,
        nodesAndOverrides,
      };
    }
  } else if (Array.isArray(nodesAndOverrides) && nodesAndOverrides.length > 0) {
    return {
      initialStatus,
      listStudentNumbers: null,
      nodesAndOverrides,
    };
  }
  return {
    initialStatus,
    listStudentNumbers: null,
    nodesAndOverrides: null,
  };
}
